import { AxiosResponse } from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';
import { GET_MSI_VALUE, GET_VESSEL_MSI_VALUE, GET_VESSEL_RECENT_SALES, GET_SUMMARY_MSI_VALUE } from './valueTypes';
import {
	getMsiValueSuccessAction, getMsiValueErrorAction,
	getVesselMsiValueSuccessAction, getVesselMsiValueErrorAction,
	getVesselRecentSalesSuccessAction, getVesselRecentSalesErrorAction,
	getSummaryMsiValueSuccessAction, getSummaryMsiValueErrorAction
} from './valueSlice';
import { PayloadAction } from '@reduxjs/toolkit';
import { authApiV2 } from '../../axiosWrapper';
import { 
    trackEvent,
    trackExceptionWithContext
} from '../../services/application-insights/appInsights';
import { 
    AppInsightsActions, 
    AppInsightsComponents, 
    AppInsightsErrorTypes 
} from '../../services/application-insights/appInsightsConstants';

function* getMSiValueSaga({ payload: imoList }: PayloadAction<number[] | undefined>) {
	const requestId = `msi-value-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
	
	try {
		trackEvent(
			AppInsightsActions.VALUE_MSI_VALUE_CALCULATION,
			{
				requestId,
				component: AppInsightsComponents.VALUE_COMPONENT,
				imoListLength: imoList?.length || 0,
				hasImoList: !!imoList
			}
		);

		const endpoint = `/msi-value`;

		const response: AxiosResponse<any> = yield call(authApiV2.post, endpoint, { imoList });

		trackEvent(
			AppInsightsActions.VALUE_DATA_FETCH_SUCCESS,
			{
				requestId,
				component: AppInsightsComponents.VALUE_COMPONENT,
				dataReceived: !!response.data?.msiValue,
				responseSize: JSON.stringify(response.data).length
			}
		);

		yield put(getMsiValueSuccessAction(response.data));
	} catch (error) {
		trackExceptionWithContext(
			error instanceof Error ? error : new Error(String(error)),
			{
				component: AppInsightsComponents.VALUE_COMPONENT,
				action: AppInsightsActions.VALUE_MSI_VALUE_CALCULATION,
				additionalData: {
					requestId,
					errorType: AppInsightsErrorTypes.API_ERROR,
					imoListLength: imoList?.length || 0
				}
			}
		);
		yield put(getMsiValueErrorAction(error));
	}
}

function* getVesselMsiValueSaga({ payload: imoList }: PayloadAction<number[] | undefined>) {
	const requestId = `vessel-msi-value-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
	
	try {
		trackEvent(
			AppInsightsActions.VALUE_DATA_FETCH_ATTEMPT,
			{
				requestId,
				component: AppInsightsComponents.VALUE_VESSEL_MSI_VALUE,
				imoListLength: imoList?.length || 0,
				hasImoList: !!imoList
			}
		);

		const endpoint = `/vessel-msi-value`;

		const response: AxiosResponse<any> = yield call(authApiV2.post, endpoint, { imoList });

		trackEvent(
			AppInsightsActions.VALUE_DATA_FETCH_SUCCESS,
			{
				requestId,
				component: AppInsightsComponents.VALUE_VESSEL_MSI_VALUE,
				dataReceived: !!response.data,
				responseSize: JSON.stringify(response.data).length
			}
		);

		yield put(getVesselMsiValueSuccessAction(response.data));
	} catch (error) {
		trackExceptionWithContext(
			error instanceof Error ? error : new Error(String(error)),
			{
				component: AppInsightsComponents.VALUE_VESSEL_MSI_VALUE,
				action: AppInsightsActions.VALUE_DATA_FETCH_ATTEMPT,
				additionalData: {
					requestId,
					errorType: AppInsightsErrorTypes.API_ERROR,
					imoListLength: imoList?.length || 0
				}
			}
		);
		yield put(getVesselMsiValueErrorAction(error));
	}
}

function* getVesselRecentSalesSaga({ payload: imoList }: PayloadAction<number[] | undefined>) {
	const requestId = `vessel-recent-sales-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
	
	try {
		trackEvent(
			AppInsightsActions.VALUE_RECENT_SALES_LOAD,
			{
				requestId,
				component: AppInsightsComponents.VALUE_COMPONENT,
				imoListLength: imoList?.length || 0,
				hasImoList: !!imoList
			}
		);

		const endpoint = `/vessel-recent-sales`;

		const response: AxiosResponse<any> = yield call(authApiV2.post, endpoint, { imoList });

		trackEvent(
			AppInsightsActions.VALUE_DATA_FETCH_SUCCESS,
			{
				requestId,
				component: AppInsightsComponents.VALUE_COMPONENT,
				dataReceived: !!response.data,
				responseSize: JSON.stringify(response.data).length
			}
		);

		yield put(getVesselRecentSalesSuccessAction(response.data));
	} catch (error) {
		trackExceptionWithContext(
			error instanceof Error ? error : new Error(String(error)),
			{
				component: AppInsightsComponents.VALUE_COMPONENT,
				action: AppInsightsActions.VALUE_RECENT_SALES_LOAD,
				additionalData: {
					requestId,
					errorType: AppInsightsErrorTypes.API_ERROR,
					imoListLength: imoList?.length || 0
				}
			}
		);
		yield put(getVesselRecentSalesErrorAction(error));
	}
}

function* getSummaryMsiValueSaga({ payload: imoList }: PayloadAction<number[] | undefined>) {
	const requestId = `summary-msi-value-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
	
	try {
		trackEvent(
			AppInsightsActions.VALUE_SUMMARY_LOAD,
			{
				requestId,
				component: AppInsightsComponents.VALUE_SUMMARY,
				imoListLength: imoList?.length || 0,
				hasImoList: !!imoList
			}
		);

		const endpoint = `/summary-msi-value`;

		const response: AxiosResponse<any> = yield call(authApiV2.post, endpoint, { imoList });

		trackEvent(
			AppInsightsActions.VALUE_DATA_FETCH_SUCCESS,
			{
				requestId,
				component: AppInsightsComponents.VALUE_SUMMARY,
				dataReceived: !!response.data,
				responseSize: JSON.stringify(response.data).length
			}
		);

		yield put(getSummaryMsiValueSuccessAction(response.data));
	} catch (error) {
		trackExceptionWithContext(
			error instanceof Error ? error : new Error(String(error)),
			{
				component: AppInsightsComponents.VALUE_SUMMARY,
				action: AppInsightsActions.VALUE_SUMMARY_LOAD,
				additionalData: {
					requestId,
					errorType: AppInsightsErrorTypes.API_ERROR,
					imoListLength: imoList?.length || 0
				}
			}
		);
		yield put(getSummaryMsiValueErrorAction(error));
	}
}

export function* watchMSIValueActions(): Generator {
	yield takeLatest(GET_MSI_VALUE, getMSiValueSaga);
	yield takeLatest(GET_VESSEL_MSI_VALUE, getVesselMsiValueSaga);
	yield takeLatest(GET_VESSEL_RECENT_SALES, getVesselRecentSalesSaga);
	yield takeLatest(GET_SUMMARY_MSI_VALUE, getSummaryMsiValueSaga);
}